import { formatAdminDate, formatAdminDateRange } from "@pages/AdminDashboard/formatAdminDate";

export const caseSearchPageSize = 30;

export interface CaseSearchFilters {
  caseId: string;
  status: string;
  from: string;
  to: string;
}

export interface CaseSearchParams {
  id?: string;
  status?: string;
  from?: string;
  to?: string;
  offset: number;
}

// A to date on its own would be windowed by the API from the first of the *current* month,
// so it starts on the first of the to date's month instead (FIND-E25).
const firstOfMonth = (isoDate: string) => `${isoDate.slice(0, 7)}-01`;

// Other blank filters are omitted so the API applies its own defaults (FIND-R4).
export const buildCaseSearchParams = (
  filters: CaseSearchFilters,
  offset: number
): CaseSearchParams => {
  const caseId = filters.caseId.trim();
  const from = filters.from || (filters.to ? firstOfMonth(filters.to) : "");

  return {
    ...(caseId && { id: caseId }),
    ...(filters.status && { status: filters.status }),
    ...(from && { from }),
    ...(filters.to && { to: filters.to }),
    offset,
  };
};

export const nextOffset = (offset: number) => offset + caseSearchPageSize;

export const previousOffset = (offset: number) => Math.max(0, offset - caseSearchPageSize);

// ISO calendar dates (YYYY-MM-DD) compare correctly as strings.
export const findDateRangeProblem = ({ from, to }: Pick<CaseSearchFilters, "from" | "to">) =>
  from && to && from > to ? "The “from” date must be on or before the “to” date." : null;

const describeCreated = ({ from, to }: Pick<CaseSearchFilters, "from" | "to">) => {
  if (from && to) return `created ${formatAdminDateRange(from, to)}`;
  if (from) return `created from ${formatAdminDate(from)}`;
  if (to) return `created up to ${formatAdminDate(to)}`;

  return "";
};

// Which filters a search used, for the empty state to echo (CVIEW-R11).
export const describeCaseSearchFilters = (filters: CaseSearchFilters): string => {
  const caseId = filters.caseId.trim();
  const parts = [
    caseId && `telehealth case ID ${caseId}`,
    filters.status && `status ${filters.status}`,
    describeCreated(filters),
  ].filter(Boolean);
  const [first, ...rest] = parts as string[];

  return first ? [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(", ") : "";
};
